package twist.mena.sports.admin.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.NavState
import twist.mena.sports.admin.state.Screen
import twist.mena.sports.shared.dto.admin.OffsetPage
import twist.mena.sports.shared.dto.admin.legacy.AdminLegacyEntityResponse
import twist.mena.sports.shared.dto.admin.legacy.CreateLegacyEntityRequest
import twist.mena.sports.shared.dto.admin.legacy.UpdateLegacyEntityRequest

class AdminLegacyCrudApi(private val baseUrl: String) {

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
    }

    private fun authHeaders(): HeadersBuilder.() -> Unit = {
        val token = AuthState.getToken()
        if (token != null) {
            append(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private suspend fun handleUnauthorized(response: HttpResponse) {
        if (response.status == HttpStatusCode.Unauthorized) {
            AuthState.logout()
            NavState.navigate(Screen.LOGIN)
        }
    }

    suspend fun listEntities(
        entityType: String,
        search: String? = null,
        offset: Int = 0,
        limit: Int = 50,
        missingTranslations: Boolean = false
    ): OffsetPage<AdminLegacyEntityResponse> {
        val response = client.get("$baseUrl/api/v1/admin/legacy/$entityType") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
            if (missingTranslations) parameter("missing_translations", true)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun createEntity(
        entityType: String,
        request: CreateLegacyEntityRequest
    ): AdminLegacyEntityResponse {
        val response = client.post("$baseUrl/api/v1/admin/legacy/$entityType") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.Created) {
            throw Exception("Create failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun updateEntity(
        entityType: String,
        id: String,
        request: UpdateLegacyEntityRequest
    ): AdminLegacyEntityResponse {
        val response = client.patch("$baseUrl/api/v1/admin/legacy/$entityType/$id") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Update failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun reorderEntity(entityType: String, id: String, direction: String): AdminLegacyEntityResponse {
        val response = client.patch("$baseUrl/api/v1/admin/legacy/$entityType/$id/reorder") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(mapOf("entity_id" to id, "direction" to direction))
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Reorder failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun hideEntity(entityType: String, id: String): AdminLegacyEntityResponse {
        val response = client.patch("$baseUrl/api/v1/admin/legacy/$entityType/$id/hide") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun unhideEntity(entityType: String, id: String): AdminLegacyEntityResponse {
        val response = client.patch("$baseUrl/api/v1/admin/legacy/$entityType/$id/unhide") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun uploadImage(entityType: String, id: String, imageBytes: ByteArray, fileName: String, contentType: String): String {
        val response = client.post("$baseUrl/api/v1/admin/legacy/$entityType/$id/image") {
            headers(authHeaders())
            setBody(
                MultiPartFormDataContent(
                    formData {
                        append("image", imageBytes, Headers.build {
                            append(HttpHeaders.ContentType, contentType)
                            append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                        })
                    }
                )
            )
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Upload failed: ${response.status}")
        }
        return response.bodyAsText()
    }

    suspend fun deleteImage(entityType: String, id: String) {
        val response = client.delete("$baseUrl/api/v1/admin/legacy/$entityType/$id/image") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
    }
}
